package org.example.project

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
data class EnableOrgRequest(val target_email: String)

@Serializable
data class OrgTransferRequest(
    val target_email: String, 
    val amount: Int,
    val settle_fiat_method: String? = null,
    val settle_fiat_proof: String? = null,
    val settle_fiat_amount: Int? = null,
    val settle_tax_country: String? = null,
    val settle_tax_id: String? = null,
    val settle_tax_rate: Int? = null,
    val settle_remark: String? = null
)

@Serializable
data class CreateLicenseKeysRequest(val bonus_units: Int, val count: Int)

@Serializable
data class RevokeLicenseKeyRequest(val key_id: String, val key_sn: String)

@Serializable
data class UseLicenseKeyRequest(
    val key_sn: String, 
    val key_pin: String,
    val ip: String,
    val device: String
)

@Serializable
data class UpdateTaxInfoRequest(
    val target_email: String,
    val tax_country: String,
    val tax_id: String
)

object BackendService {
    val httpClient = HttpClient {
        expectSuccess = false

        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }

        defaultRequest {
            url(BACKEND_URL)
            contentType(ContentType.Application.Json)
        }

        install(Auth) {
            bearer {
                loadTokens {
                    val token = getCurrentUserIdToken()
                    if (token != null) {
                        BearerTokens(token, "")
                    } else {
                        null
                    }
                }
            }
        }

        install(Logging) {
            level = LogLevel.BODY
        }
    }

    suspend fun syncWithBackend(): SyncWithBackendResult {
        return runCatching {
            val response = httpClient.post("/api/v1/account/sync")
            val status = response.status.value
            if (response.status.isSuccess()) {
                SyncWithBackendResult.Success
            } else {
                val apiResponse = try { response.body<SimpleResponse>() } catch (e: Exception) { null }
                val errorMsg = apiResponse?.message ?: "Failed with status $status"
                SyncWithBackendResult.Error(errorMsg)
            }
        }.getOrElse { e ->
            SyncWithBackendResult.Error(e.message ?: e.toString())
        }
    }

    suspend fun userCheck(targetEmail: String): UserCheckResult {
        return runCatching {
            val response = httpClient.get("/api/v1/user/check") {
                url { parameters.append("target_email", targetEmail) }
            }
            val apiResponse = response.body<ApiResponse<UserCheckInfo>>()
            if (response.status.isSuccess()) {
                UserCheckResult.Success(apiResponse.data!!)
            } else {
                UserCheckResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            UserCheckResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getOrgInfo(targetEmail: String): GetOrgInfoResult {
        return runCatching {
            val response = httpClient.get("/api/v1/org/check") {
                url { parameters.append("target_email", targetEmail) }
            }
            val apiResponse = response.body<ApiResponse<SystemProfileDocument>>()
            if (response.status.isSuccess()) {
                GetOrgInfoResult.Success(apiResponse.data!!)
            } else {
                GetOrgInfoResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            GetOrgInfoResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun checkTaxInfo(targetEmail: String): CheckTaxInfoResult {
        return runCatching {
            val response = httpClient.get("/api/v1/tax-info/check") {
                url { parameters.append("target_email", targetEmail) }
            }
            val apiResponse = response.body<ApiResponse<TaxInfo>>()
            if (response.status.isSuccess()) {
                CheckTaxInfoResult.Success(apiResponse.data!!)
            } else {
                CheckTaxInfoResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            CheckTaxInfoResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun updateTaxInfo(targetEmail: String, taxCountry: String, taxId: String): UpdateTaxInfoResult {
        return runCatching {
            val response = httpClient.post("/api/v1/tax-info/update") {
                setBody(UpdateTaxInfoRequest(targetEmail, taxCountry, taxId))
            }
            if (response.status.isSuccess()) {
                UpdateTaxInfoResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                UpdateTaxInfoResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            UpdateTaxInfoResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun enableOrg(email: String): EnableOrgResult {
        return runCatching {
            val response = httpClient.post("/api/v1/org/enable") {
                setBody(EnableOrgRequest(email))
            }
            if (response.status.isSuccess()) {
                EnableOrgResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                EnableOrgResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            EnableOrgResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun orgProvision(
        targetEmail: String, 
        amount: Int,
        settleFiatMethod: String?,
        settleFiatProof: String?,
        settleFiatAmount: Int?,
        settleTaxCountry: String?,
        settleTaxId: String?,
        settleTaxRate: Int?,
        settleRemark: String?
    ): OrgProvisionResult {
        return runCatching {
            val response = httpClient.post("/api/v1/org/grant") {
                setBody(OrgTransferRequest(
                    target_email = targetEmail, 
                    amount = amount,
                    settle_fiat_method = settleFiatMethod,
                    settle_fiat_proof = settleFiatProof,
                    settle_fiat_amount = settleFiatAmount,
                    settle_tax_country = settleTaxCountry,
                    settle_tax_id = settleTaxId,
                    settle_tax_rate = settleTaxRate,
                    settle_remark = settleRemark
                ))
            }
            if (response.status.isSuccess()) {
                OrgProvisionResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                OrgProvisionResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            OrgProvisionResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun orgReclaim(targetEmail: String, amount: Int): OrgReclaimResult {
        return runCatching {
            val response = httpClient.post("/api/v1/org/reclaim") {
                setBody(OrgTransferRequest(targetEmail, amount))
            }
            if (response.status.isSuccess()) {
                OrgReclaimResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                OrgReclaimResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            OrgReclaimResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getLicensePackages(): LicensePackageListResult {
        return runCatching {
            val response = httpClient.get("/api/v1/org/license_packages")
            val apiResponse = response.body<ApiResponse<List<LicensePackageDocument>>>()
            if (response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created) {
                LicensePackageListResult.Success(apiResponse.data!!)
            } else {
                LicensePackageListResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            LicensePackageListResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getIapPackages(): GetIapPackagesResult {
        return runCatching {
            val response = httpClient.get("/api/v1/org/iap_packages")
            val apiResponse = response.body<ApiResponse<List<IapPackageDocument>>>()
            if (response.status.isSuccess()) {
                GetIapPackagesResult.Success(apiResponse.data ?: emptyList())
            } else {
                GetIapPackagesResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            GetIapPackagesResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getOrgLevels(): OrgLevelListResult {
        return runCatching {
            val response = httpClient.get("/api/v1/org/levels")
            val apiResponse = response.body<ApiResponse<List<OrgTierDocument>>>()
            if (response.status.isSuccess()) {
                OrgLevelListResult.Success(apiResponse.data!!)
            } else {
                OrgLevelListResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            OrgLevelListResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun createLicenseKeys(bonusUnits: Int, count: Int): CreateLicenseKeyResult {
        return runCatching {
            val response = httpClient.post("/api/v1/license-keys/create") {
                setBody(CreateLicenseKeysRequest(bonusUnits, count))
            }
            if (response.status.isSuccess()) {
                CreateLicenseKeyResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                CreateLicenseKeyResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            CreateLicenseKeyResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun listLicenseKeys(limit: Int, status: LicenseKeyStatus?, cursor: String?): LicenseKeyListResult {
        return runCatching {
            val response = httpClient.get("/api/v1/license-keys/list") {
                url {
                    parameters.append("limit", limit.toString())
                    if (cursor != null) parameters.append("cursor", cursor)
                    if (status != null) parameters.append("status", status.value)
                }
            }
            val apiResponse = response.body<ApiResponse<LicenseKeyListResult>>()
            if (response.status.isSuccess()) {
                val data = apiResponse.data!!
                LicenseKeyListResult(data.data, data.next_cursor)
            } else {
                LicenseKeyListResult(emptyList(), null)
            }
        }.getOrElse { e ->
            LicenseKeyListResult(emptyList(), null)
        }
    }

    suspend fun checkLicenseKey(codeSn: String): CheckLicenseKeyResult {
        return runCatching {
            val response = httpClient.get("/api/v1/license-keys/check") {
                url { parameters.append("key_sn", codeSn) }
            }
            val apiResponse = response.body<ApiResponse<LicenseKeyPublicInfo>>()
            if (response.status.isSuccess()) {
                CheckLicenseKeyResult.Success(apiResponse.data!!)
            } else {
                CheckLicenseKeyResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            CheckLicenseKeyResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun useLicenseKey(codeSn: String, codePin: String): UseLicenseKeyResult {
        return runCatching {
            val ipResponse = httpClient.get("/api/v1/my-ip/get").body<ApiResponse<IpInfo>>()
            val ipAddress = ipResponse.data!!.ip
            val userAgent = getUserAgent()

            val response = httpClient.post("/api/v1/license-keys/claim") {
                setBody(UseLicenseKeyRequest(
                    key_sn = codeSn, 
                    key_pin = codePin, 
                    ip = ipAddress, 
                    device = userAgent
                ))
            }
            val apiResponse = response.body<ApiResponse<LicenseKeyPublicInfo>>()
            if (response.status.isSuccess()) {
                UseLicenseKeyResult.Success(apiResponse.data!!)
            } else {
                UseLicenseKeyResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            UseLicenseKeyResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun revokeLicenseKey(codeId: String, codeSn: String): RevokeLicenseKeyResult {
        return runCatching {
            val response = httpClient.post("/api/v1/license-keys/revoke") {
                setBody(RevokeLicenseKeyRequest(codeId, codeSn))
            }
            if (response.status.isSuccess()) {
                RevokeLicenseKeyResult.Success
            } else {
                val apiResponse = response.body<SimpleResponse>()
                RevokeLicenseKeyResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            RevokeLicenseKeyResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getTaxCountries(): GetTaxCountriesResult {
        return runCatching {
            val response = httpClient.get("/api/v1/tax-countries/get")
            val apiResponse = response.body<ApiResponse<List<String>>>()
            if (response.status.isSuccess()) {
                GetTaxCountriesResult.Success(apiResponse.data ?: emptyList())
            } else {
                GetTaxCountriesResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            GetTaxCountriesResult.Error(e.message ?: "Network error")
        }
    }

    suspend fun getFiatPaymentMethods(): GetFiatPaymentMethodsResult {
        return runCatching {
            val response = httpClient.get("/api/v1/fiat-payment-methods/get")
            val apiResponse = response.body<ApiResponse<List<String>>>()
            if (response.status.isSuccess()) {
                GetFiatPaymentMethodsResult.Success(apiResponse.data ?: emptyList())
            } else {
                GetFiatPaymentMethodsResult.Error(apiResponse.message)
            }
        }.getOrElse { e ->
            GetFiatPaymentMethodsResult.Error(e.message ?: "Network error")
        }
    }
}